import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import Switch from "../../../components/Switch"
import Button from "../../../components/Button"
import InputWithLabel from "../../../components/InputWithLabel"
import LoaderDots from "../../../components/LoaderDots"
import WhiteSpace from "../../../components/Whitespace"
import Icon from "../../../Icon"
import { useNotification } from "../../../modules/Notification"
import { IconButton } from "../../Users/userStyles"
import { isGeneralOk, sendChargerCommand } from "./chargerCommand"
import { ButtonRow, CenteredLoader, Divider, FieldError, HelpText, MutedMessage, NumberInput, PanelTitle, Row, RowLabel, RowValue, Section, StatusBadge } from "./integrationStyles"

// HTTP API settings, following the apps' HttpAPIView: msg 84 reads the config and status, msg 19 writes
// the http_api block, msg 83 sets the basic-auth credentials (VLT-2695).

interface ApiStatusReply {
  http_api?: { enabled?: boolean, port?: number, http_auth?: number }
  api_status?: number
  api_version?: number
  user_names?: string[]
}

const DEFAULT_PORT = 5059
const RESERVED_PORTS = [44444, 44445]
const CREDENTIAL_MAX_LENGTH = 32

function portErrorKey(port: number): string | null {
  if (!Number.isInteger(port) || port < 1025 || port > 65535) return 'charger_portMustBeBetween1025And65535'
  if (RESERVED_PORTS.includes(port)) return 'charger_thisPortIsReserved'
  return null
}

function apiStatusLabel(status: number | undefined): { key: string, tone: 'success' | 'danger' | 'neutral' } {
  switch (status) {
    case 0: return { key: 'charger_disabled', tone: 'neutral' }
    case 1: return { key: 'charger_initialized', tone: 'success' }
    case 2: return { key: 'charger_initializationError', tone: 'danger' }
    default: return { key: 'charger_notAvailable', tone: 'neutral' }
  }
}

export default function HttpApiPanel({ chargerId }: { chargerId: string }) {
  const { t } = useTranslation()
  const { addNotification } = useNotification()

  const [reply, setReply] = useState<ApiStatusReply | null>()
  const [enabled, setEnabled] = useState<boolean>(false)
  const [port, setPort] = useState<string>(String(DEFAULT_PORT))
  const [authEnabled, setAuthEnabled] = useState<boolean>(false)
  const [username, setUsername] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [isSaving, setIsSaving] = useState<boolean>(false)

  async function fetchStatus() {
    try {
      const status: ApiStatusReply = await sendChargerCommand(chargerId, { msg_type: 84 })
      setReply(status)
      setEnabled(!!status.http_api?.enabled)
      setPort(String(status.http_api?.port ?? DEFAULT_PORT))
      setAuthEnabled(status.http_api?.http_auth === 1)
      setUsername(status.user_names?.[0] ?? '')
      setPassword('')
    } catch (err) {
      console.log('Unable to read the HTTP API status', err)
      setReply(null)
    }
  }

  useEffect(() => {
    fetchStatus()
  }, [chargerId])

  const portNumber = Number(port)
  const portError = enabled ? portErrorKey(portNumber) : null
  // As in the apps: authentication needs a password, since the charger never hands the stored one back.
  const canSave = !enabled || (!portError && (!authEnabled || password.length > 0))

  async function save() {
    if (!canSave || isSaving) return
    setIsSaving(true)
    try {
      // Turning the API off leaves the port and auth settings as they are, rather than writing whatever
      // is typed in the (unvalidated) port field.
      const httpApi = enabled ? { enabled, port: portNumber, http_auth: authEnabled ? 1 : 0 } : { enabled }
      if (!isGeneralOk(await sendChargerCommand(chargerId, { msg_type: 19, http_api: httpApi }))) throw new Error('http_api config refused')
      if (enabled && authEnabled) {
        // The apps give the config write a second to settle before sending the credentials.
        await new Promise(resolve => setTimeout(resolve, 1000))
        if (!isGeneralOk(await sendChargerCommand(chargerId, { msg_type: 83, usr: username, pwd: password }))) throw new Error('credentials refused')
      }
      addNotification(t('charger_networkSettingsSavedSuccessfully'), 'TEXT')
    } catch (err) {
      console.log('Unable to save the HTTP API settings', err)
      addNotification(t('charger_failedToSaveNetworkSettings'), 'TEXT', 'error')
    }
    await fetchStatus()
    setIsSaving(false)
  }

  async function deleteCredentials() {
    try {
      if (!isGeneralOk(await sendChargerCommand(chargerId, { msg_type: 83, usr: '', pwd: '' }))) throw new Error('credentials refused')
      setUsername('')
      setPassword('')
    } catch (err) {
      console.log('Unable to delete the HTTP API credentials', err)
      addNotification(t('charger_failedToSaveNetworkSettings'), 'TEXT', 'error')
    }
  }

  const status = apiStatusLabel(reply?.api_status)

  return (
    <>
      <PanelTitle>{t('charger_httpApi')}</PanelTitle>
      {reply === undefined && <CenteredLoader><LoaderDots /></CenteredLoader>}
      {reply === null && <Section><MutedMessage>{t('charger_couldNotLoadTheSettingsFromTheCharger')}</MutedMessage></Section>}
      {reply && <>
        <Section>
          <Row>
            <RowLabel>{t('charger_enabled')}</RowLabel>
            <Switch ariaLabel={t('charger_enabled')} toggled={enabled} setToggled={setEnabled} />
          </Row>

          {enabled && <>
            <Divider />
            <Row>
              <RowLabel>{t('charger_port')}</RowLabel>
              <NumberInput type="number" aria-label={t('charger_port')} $isError={!!portError} value={port} onChange={e => setPort(e.target.value)} />
            </Row>
            {portError && <FieldError>{t(portError)}</FieldError>}

            <Divider />
            <Row>
              <RowLabel>{t('charger_apiStatus')}</RowLabel>
              <StatusBadge $tone={status.tone}>{t(status.key)}</StatusBadge>
            </Row>
            <Row>
              <RowLabel>{t('charger_apiVersion')}</RowLabel>
              <RowValue>{reply.api_version ?? '-'}</RowValue>
            </Row>

            <Divider />
            <Row>
              <RowLabel>{t('charger_enableAuthentication')}</RowLabel>
              <Switch ariaLabel={t('charger_enableAuthentication')} toggled={authEnabled} setToggled={setAuthEnabled} />
            </Row>
            {authEnabled && <>
              <WhiteSpace height="12px" />
              <InputWithLabel label={t('charger_username')} width="100%" value={username} onInput={value => setUsername(value.trim().slice(0, CREDENTIAL_MAX_LENGTH))} />
              <WhiteSpace height="12px" />
              <InputWithLabel label={t('charger_password')} width="100%" isPassword value={password} onInput={value => setPassword(value.trim().slice(0, CREDENTIAL_MAX_LENGTH))} />
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <HelpText style={{ flex: 1 }}>{t('charger_writeDownTheUsernameAndPasswordTheyWontBeDisplayedLater')}</HelpText>
                {username && (
                  <IconButton aria-label={t('charger_delete')} onClick={deleteCredentials} style={{ marginTop: '4px' }}>
                    <Icon path="general.trash" width="16px" height="16px" color="danger" />
                  </IconButton>
                )}
              </div>
            </>}
          </>}
        </Section>

        <ButtonRow style={{ opacity: canSave ? 1 : 0.5, pointerEvents: canSave ? 'auto' : 'none' }}>
          <Button label={isSaving ? <LoaderDots /> : t('charger_save')} width="120px" onclick={save} />
        </ButtonRow>
      </>}
    </>
  )
}
